import { translate } from '../../i18n/index.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  adminApi,
  ownerApi,
  type DashboardTrendPoint,
  type DashboardResponse,
  type PlatformDashboard,
} from '../../api/admin.api.js';
import { Spinner } from '../../components/design/AtomFitUI.js';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { useAuth } from '../../hooks/useAuth.js';

type DashboardData = DashboardResponse;
type Metric = 'revenue' | 'reservations';

interface StatCard {
  label: string;
  value: string;
  detail: string;
  accent?: boolean;
  href?: string;
}

const moneyFormatter = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  maximumFractionDigits: 0,
});

function formatMoney(value: number) {
  return moneyFormatter.format(value / 100);
}

interface DateRange {
  from: string;
  to: string;
}

interface DatePreset {
  id: 'this-week' | 'this-month' | 'last-7' | 'last-30' | 'last-90';
  label: string;
}

const datePresets: DatePreset[] = [
  { id: 'this-week', label: translate('pages.admin.admin_dashboard.date_presets.time') },
  { id: 'this-month', label: translate('pages.admin.admin_dashboard.date_presets.time_2') },
  { id: 'last-7', label: translate('pages.admin.admin_dashboard.date_presets.label') },
  { id: 'last-30', label: translate('pages.admin.admin_dashboard.date_presets.label_2') },
  { id: 'last-90', label: translate('pages.admin.admin_dashboard.date_presets.label_3') },
];

const weekdayLabels = [
  translate('pages.admin.admin_dashboard.module.weekday_labels'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_2'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_3'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_4'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_5'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_6'),
  translate('pages.admin.admin_dashboard.module.weekday_labels_7'),
];

const compactDateFormatter = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'short' });
const monthFormatter = new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' });

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateFromKey(value: string) {
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function rangeForPreset(id: DatePreset['id']): DateRange {
  const today = dateFromKey(new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' }));
  const end = dateKey(today);
  if (id === 'this-week') {
    const mondayOffset = (today.getDay() + 6) % 7;
    return { from: dateKey(addDays(today, -mondayOffset)), to: end };
  }
  if (id === 'this-month')
    return { from: dateKey(new Date(today.getFullYear(), today.getMonth(), 1)), to: end };
  const days = id === 'last-7' ? 7 : id === 'last-30' ? 30 : 90;
  return { from: dateKey(addDays(today, -(days - 1))), to: end };
}

function defaultRange() {
  return rangeForPreset('last-7');
}

function rangeLabel(range: DateRange) {
  const preset = datePresets.find((item) => {
    const presetRange = rangeForPreset(item.id);
    return presetRange.from === range.from && presetRange.to === range.to;
  });
  if (preset) return preset.label;
  return `${compactDateFormatter.format(dateFromKey(range.from))} — ${compactDateFormatter.format(dateFromKey(range.to))}`;
}

function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(value);
  const [visibleMonth, setVisibleMonth] = useState(() => dateFromKey(value.to));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const today = dateFromKey(new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' }));
  const todayKey = dateKey(today);

  useEffect(() => {
    function closeOnOutsideClick(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    if (isOpen) window.addEventListener('pointerdown', closeOnOutsideClick);
    return () => window.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  const calendarDays = useMemo(() => {
    const first = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const mondayOffset = (first.getDay() + 6) % 7;
    return Array.from(
      { length: 42 },
      (_, index) =>
        new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), index - mondayOffset + 1),
    );
  }, [visibleMonth]);

  const activePreset = datePresets.find((item) => {
    const range = rangeForPreset(item.id);
    return range.from === value.from && range.to === value.to;
  })?.id;
  const isAtCurrentMonth =
    visibleMonth.getFullYear() === today.getFullYear() &&
    visibleMonth.getMonth() === today.getMonth();

  function openPicker() {
    setDraftRange(value);
    setVisibleMonth(dateFromKey(value.to));
    setSelectingEnd(false);
    setSelectionError('');
    setIsOpen((open) => !open);
  }

  function choosePreset(id: DatePreset['id']) {
    const nextRange = rangeForPreset(id);
    setDraftRange(nextRange);
    onChange(nextRange);
    setIsOpen(false);
  }

  function chooseDate(date: Date) {
    const nextDate = dateKey(date);
    if (nextDate > todayKey) return;

    if (!selectingEnd) {
      setDraftRange({ from: nextDate, to: nextDate });
      setSelectingEnd(true);
      setSelectionError('');
      return;
    }

    const nextRange =
      nextDate < draftRange.from
        ? { from: nextDate, to: draftRange.from }
        : { from: draftRange.from, to: nextDate };
    const rangeLength =
      Math.round(
        (dateFromKey(nextRange.to).getTime() - dateFromKey(nextRange.from).getTime()) / 86_400_000,
      ) + 1;
    if (rangeLength > 90) {
      setSelectionError(
        translate('pages.admin.admin_dashboard.choose_date.set_selection_error.time'),
      );
      return;
    }
    setDraftRange(nextRange);
    setSelectingEnd(false);
    setSelectionError('');
    onChange(nextRange);
    setIsOpen(false);
  }

  return (
    <div className="af-date-range-picker" ref={rootRef}>
      <button
        type="button"
        className={`af-date-range-trigger${isOpen ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={openPicker}
      >
        <IcoClock />
        <span>{rangeLabel(value)}</span>
        <IcoChevron />
      </button>
      {isOpen && (
        <div
          className="af-date-range-popover"
          role="dialog"
          aria-label={translate(
            'pages.admin.admin_dashboard.date_range_picker.div_date_range_popover.aria_label_content',
          )}
        >
          <div className="af-date-range-presets">
            {datePresets.map((preset) => (
              <button
                type="button"
                key={preset.id}
                className={activePreset === preset.id ? 'is-active' : ''}
                onClick={() => choosePreset(preset.id)}
              >
                {preset.label}
                {activePreset === preset.id && <IcoCheckmark />}
              </button>
            ))}
          </div>
          <div className="af-date-range-calendar">
            <div className="af-date-range-month-head">
              <strong>{monthFormatter.format(visibleMonth)}</strong>
              <div>
                <button
                  type="button"
                  aria-label={translate(
                    'pages.admin.admin_dashboard.date_range_picker.button.aria_label_previous',
                  )}
                  onClick={() =>
                    setVisibleMonth(
                      (month) => new Date(month.getFullYear(), month.getMonth() - 1, 1),
                    )
                  }
                >
                  <IcoArrow direction="left" />
                </button>
                <button
                  type="button"
                  aria-label={translate(
                    'pages.admin.admin_dashboard.date_range_picker.button.aria_label_next',
                  )}
                  disabled={isAtCurrentMonth}
                  onClick={() =>
                    setVisibleMonth(
                      (month) => new Date(month.getFullYear(), month.getMonth() + 1, 1),
                    )
                  }
                >
                  <IcoArrow direction="right" />
                </button>
              </div>
            </div>
            <div className="af-date-range-weekdays">
              {weekdayLabels.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="af-date-range-days">
              {calendarDays.map((date) => {
                const key = dateKey(date);
                const isDisabled = key > todayKey;
                const isRangeStart = key === draftRange.from;
                const isRangeEnd = key === draftRange.to;
                const isWithinRange = key > draftRange.from && key < draftRange.to;
                const isOtherMonth = date.getMonth() !== visibleMonth.getMonth();
                return (
                  <button
                    type="button"
                    key={key}
                    disabled={isDisabled}
                    aria-label={compactDateFormatter.format(date)}
                    className={`${isRangeStart || isRangeEnd ? 'is-selected' : ''}${isWithinRange ? ' is-in-range' : ''}${isOtherMonth ? ' is-other-month' : ''}`}
                    onClick={() => chooseDate(date)}
                  >
                    <span>{date.getDate()}</span>
                  </button>
                );
              })}
            </div>
            {selectingEnd && (
              <p className="af-date-range-hint">
                {translate(
                  'pages.admin.admin_dashboard.date_range_picker.p_date_range_hint.select',
                )}
              </p>
            )}
            {selectionError && <p className="af-date-range-error">{selectionError}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function IcoClock() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" />
      <path d="M10 5.5v4.8h3.5" />
    </svg>
  );
}

function IcoChevron() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

function IcoArrow({ direction }: { direction: 'left' | 'right' }) {
  return direction === 'left' ? (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m9.5 3.5-4 4 4 4" />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m6.5 3.5 4 4-4 4" />
    </svg>
  );
}

function IcoCheckmark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m3 8.2 3 3L13 4.5" />
    </svg>
  );
}

function isPlatformDashboard(data: DashboardData): data is PlatformDashboard {
  return 'totalTenants' in data;
}

function LineChart({ metric, trend }: { metric: Metric; trend: DashboardTrendPoint[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const title =
    metric === 'revenue'
      ? translate('pages.admin.admin_dashboard.line_chart.pricing')
      : translate('pages.admin.admin_dashboard.line_chart.booking');
  const description =
    metric === 'revenue'
      ? translate('pages.admin.admin_dashboard.line_chart.payment')
      : translate('report.createdDetail');
  const colour = metric === 'revenue' ? 'var(--af-accent)' : 'var(--af-success)';
  const gradient =
    metric === 'revenue' ? 'dashboardRevenueGradient' : 'dashboardReservationGradient';

  const chart = useMemo(() => {
    const width = 620;
    const height = 200;
    const values = trend.map((item) => item[metric]);
    const max = Math.max(...values, 1);
    const step = trend.length > 1 ? width / (trend.length - 1) : width;
    const points = trend.map((item, index) => ({
      ...item,
      value: item[metric],
      x: index * step,
      y: height - (item[metric] / max) * (height - 16) - 8,
    }));
    const line = points.map((point) => `${point.x},${point.y}`).join('L');
    return {
      width,
      height,
      max,
      points,
      line: `M${line}`,
      area: `M${line}L${width},${height}L0,${height}Z`,
    };
  }, [metric, trend]);

  const activePoint = activeIndex === null ? null : chart.points[activeIndex];
  const labelInterval = trend.length > 10 ? Math.ceil(trend.length / 5) : 1;

  return (
    <section
      className="af-dashboard-chart af-card"
      aria-label={translate(
        'pages.admin.admin_dashboard.line_chart.section_dashboard_chart_card.aria_label_content',
        { title: title },
      )}
    >
      <div className="af-dashboard-chart-heading">
        <div>
          <p className="af-dashboard-kicker">
            {translate('pages.admin.admin_dashboard.line_chart.p_dashboard_kicker.description')}
          </p>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className={`af-dashboard-chart-key af-dashboard-chart-key--${metric}`}>
          <span /> {title}
        </span>
      </div>

      <div className="af-dashboard-plot">
        <svg
          viewBox={`0 0 ${chart.width} ${chart.height + 34}`}
          role="img"
          onMouseLeave={() => setActiveIndex(null)}
        >
          <defs>
            <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colour} stopOpacity="0.26" />
              <stop offset="100%" stopColor={colour} stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
            <line
              key={tick}
              x1="0"
              x2={chart.width}
              y1={8 + tick * (chart.height - 16)}
              y2={8 + tick * (chart.height - 16)}
              className="af-dashboard-gridline"
            />
          ))}
          <path d={chart.area} fill={`url(#${gradient})`} />
          <path
            d={chart.line}
            fill="none"
            stroke={colour}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {chart.points.map((point, index) => (
            <g key={point.date} onMouseEnter={() => setActiveIndex(index)}>
              <circle cx={point.x} cy={point.y} r="11" fill="transparent" />
              <circle
                cx={point.x}
                cy={point.y}
                r={activeIndex === index ? '5.5' : '3.5'}
                fill={colour}
                stroke="var(--af-card-bg)"
                strokeWidth="2"
              />

              {(index % labelInterval === 0 || index === chart.points.length - 1) && (
                <text
                  x={point.x}
                  y={chart.height + 24}
                  textAnchor="middle"
                  className="af-dashboard-axis-label"
                >
                  {point.label}
                </text>
              )}
            </g>
          ))}
          {activePoint && (
            <g
              transform={`translate(${Math.min(Math.max(activePoint.x - 55, 0), chart.width - 110)}, ${Math.max(activePoint.y - 46, 0)})`}
            >
              <rect width="110" height="34" rx="5" className="af-dashboard-tooltip" />
              <text x="55" y="14" textAnchor="middle" className="af-dashboard-tooltip-label">
                {activePoint.label}
              </text>
              <text x="55" y="27" textAnchor="middle" className="af-dashboard-tooltip-value">
                {metric === 'revenue'
                  ? formatMoney(activePoint.value)
                  : translate(
                      'pages.admin.admin_dashboard.line_chart.text_dashboard_tooltip_value.booking',
                      { value1: activePoint.value },
                    )}
              </text>
            </g>
          )}
        </svg>
      </div>
      <div className="af-dashboard-chart-footer">
        <span>{'0'}</span>
        <span>
          {metric === 'revenue'
            ? formatMoney(chart.max)
            : translate('pages.admin.admin_dashboard.line_chart.span.booking', {
                value1: chart.max,
              })}
        </span>
      </div>
    </section>
  );
}

function LoadingState() {
  return (
    <div className="af-card af-dashboard-state">
      <Spinner />
      {translate('pages.admin.admin_dashboard.loading_state.div_card_dashboard_state.loading')}
    </div>
  );
}

export function AdminDashboard() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [franchiseSlug, setFranchiseSlug] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<Array<{ name: string; slug: string }>>([]);
  const [error, setError] = useState(false);

  const loadDashboard = () => {
    setError(false);
    setDashboard(null);
    const request = isPlatform
      ? adminApi.getDashboard(appliedRange, franchiseSlug ?? undefined)
      : ownerApi.getDashboard(appliedRange);
    void request.then((response) => setDashboard(response.data)).catch(() => setError(true));
  };

  useEffect(() => {
    loadDashboard();
    // The active role and selected period define the data scope.
  }, [isPlatform, appliedRange, franchiseSlug]);

  useEffect(() => {
    if (!isPlatform) {
      setFranchises([]);
      return;
    }

    let cancelled = false;
    async function loadFranchises() {
      try {
        const firstPage = await adminApi.listTenants(1, 100);
        const remainingPages = Array.from(
          { length: Math.max(0, firstPage.data.meta.totalPages - 1) },
          (_, index) => adminApi.listTenants(index + 2, 100),
        );
        const pages = [firstPage, ...(await Promise.all(remainingPages))];
        if (cancelled) return;

        setFranchises(
          pages
            .flatMap((response) => response.data.data)
            .filter((tenant) => tenant.isActive)
            .map((tenant) => ({
              name:
                tenant.type === 'PLATFORM' ? `${tenant.name} · platformová franšíza` : tenant.name,
              slug: tenant.slug,
            })),
        );
      } catch {
        if (!cancelled) setFranchises([]);
      }
    }
    void loadFranchises();
    return () => {
      cancelled = true;
    };
  }, [isPlatform]);

  if (role === 'GYM_STAFF') return <Navigate to="/admin/bookings" replace />;

  const selectedRevenue = dashboard
    ? dashboard.trend.reduce((total, point) => total + point.revenue, 0)
    : 0;
  const selectedReservations = dashboard
    ? dashboard.trend.reduce((total, point) => total + point.reservations, 0)
    : 0;

  const stats: StatCard[] = dashboard
    ? isPlatformDashboard(dashboard)
      ? [
          {
            label: translate('pages.admin.admin_dashboard.stats.pricing'),
            value: formatMoney(selectedRevenue),
            detail: translate('pages.admin.admin_dashboard.stats.detail'),
            accent: true,
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.booking'),
            value: selectedReservations.toLocaleString('cs-CZ'),
            detail: translate('pages.admin.admin_dashboard.stats.detail_2'),
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.gym_active'),
            value: dashboard.totalActiveGyms.toLocaleString('cs-CZ'),
            detail: translate('pages.admin.admin_dashboard.stats.tenant'),
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.tenant_2'),
            value: dashboard.totalTenants.toLocaleString('cs-CZ'),
            detail: translate('pages.admin.admin_dashboard.stats.registration'),
          },
        ]
      : [
          {
            label: translate('pages.admin.admin_dashboard.stats.tenant_pricing'),
            value: formatMoney(selectedRevenue),
            detail: translate('pages.admin.admin_dashboard.stats.detail_3'),
            accent: true,
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.booking_2'),
            value: selectedReservations.toLocaleString('cs-CZ'),
            detail: translate('pages.admin.admin_dashboard.stats.detail_4'),
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.gym'),
            value: dashboard.totalGyms.toLocaleString('cs-CZ'),
            detail: translate('pages.admin.admin_dashboard.stats.detail_5'),
          },
          {
            label: translate('pages.admin.admin_dashboard.stats.booking_3'),
            value: formatMoney(selectedReservations ? selectedRevenue / selectedReservations : 0),
            detail: translate('pages.admin.admin_dashboard.stats.detail_6'),
          },
        ]
    : [];

  const metricLink = (metric: string) => {
    const params = new URLSearchParams({
      range: 'all',
      metric,
      metricFrom: appliedRange.from,
      metricTo: appliedRange.to,
    });
    if (franchiseSlug) params.set('tenantSlug', franchiseSlug);
    return `/admin/bookings?${params.toString()}`;
  };
  if (dashboard) {
    stats[1] = {
      label: translate('report.created'),
      value: String(dashboard.metrics.created),
      detail: translate('report.createdDetail'),
      href: metricLink('created'),
    };
    for (const metric of ['cancelled', 'attended', 'noShow'] as const) {
      stats.push({
        label: translate(`report.${metric}`),
        value: String(dashboard.metrics[metric]),
        detail: translate(`report.${metric}Detail`),
        href: metricLink(metric),
      });
    }
    const registrations = new URLSearchParams({
      role: 'CUSTOMER',
      registeredFrom: appliedRange.from,
      registeredTo: appliedRange.to,
    });
    if (franchiseSlug) registrations.set('tenantSlug', franchiseSlug);
    stats.push({
      label: translate('report.registrations'),
      value: String(dashboard.metrics.registrations),
      detail: translate('report.registrationsDetail'),
      href: `/admin/users?${registrations.toString()}`,
    });
  }

  const franchiseName = franchises.find((franchise) => franchise.slug === franchiseSlug)?.name;
  const dashboardTitle = isPlatform
    ? franchiseSlug
      ? translate('pages.admin.admin_dashboard.admin_dashboard.dashboard_title', {
          value1: franchiseName ?? 'Franchise',
        })
      : translate('pages.admin.admin_dashboard.admin_dashboard.dashboard_title_2')
    : translate('pages.admin.admin_dashboard.admin_dashboard.dashboard_title_3');

  return (
    <AdminLayout
      title={dashboardTitle}
      eyebrow={
        isPlatform && !franchiseSlug
          ? translate('pages.admin.admin_dashboard.admin_dashboard.admin_layout.eyebrow')
          : translate('pages.admin.admin_dashboard.admin_dashboard.admin_layout.eyebrow_2')
      }
    >
      <div className="af-dashboard-intro">
        <div>
          <p>
            {isPlatform
              ? franchiseSlug
                ? translate('pages.admin.admin_dashboard.admin_dashboard.p.tenant')
                : translate('pages.admin.admin_dashboard.admin_dashboard.p.tenant_2')
              : translate('pages.admin.admin_dashboard.admin_dashboard.p.tenant_3')}
          </p>
        </div>
        <div className="af-dashboard-controls">
          {isPlatform && (
            <label className="af-dashboard-scope">
              <span>{translate('pages.admin.admin_dashboard.admin_dashboard.span.dashboard')}</span>
              <select
                value={franchiseSlug ?? ''}
                onChange={(event) => setFranchiseSlug(event.target.value || null)}
              >
                <option value="">
                  {translate('pages.admin.admin_dashboard.admin_dashboard.option.tenant')}
                </option>
                {franchises.map((franchise) => (
                  <option key={franchise.slug} value={franchise.slug}>
                    {franchise.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <DateRangePicker value={appliedRange} onChange={setAppliedRange} />
        </div>
      </div>

      {error ? (
        <div className="af-card af-dashboard-state af-dashboard-state--error">
          <span>{translate('pages.admin.admin_dashboard.admin_dashboard.span.error')}</span>
          <button type="button" className="af-outline-btn" onClick={loadDashboard}>
            {translate('pages.admin.admin_dashboard.admin_dashboard.button_outline_btn.retry')}
          </button>
        </div>
      ) : !dashboard ? (
        <LoadingState />
      ) : (
        <>
          <div className="af-dashboard-stats">
            {stats.map((stat) => (
              <article className="af-card af-dashboard-stat" key={stat.label}>
                <p>{stat.label}</p>
                <strong className={stat.accent ? 'is-accent' : ''}>
                  {stat.href ? (
                    <Link
                      to={stat.href}
                      style={{ color: 'inherit' }}
                      aria-label={`${stat.label}: ${stat.value}`}
                    >
                      {stat.value}
                    </Link>
                  ) : (
                    stat.value
                  )}
                </strong>
                <span>{stat.detail}</span>
              </article>
            ))}
          </div>

          <div className="af-dashboard-charts">
            <LineChart metric="revenue" trend={dashboard.trend} />
            <LineChart metric="reservations" trend={dashboard.trend} />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
